'use client'

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import * as z from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useRef } from "react";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
import ReCAPTCHA from "react-google-recaptcha";
import { sendEmail } from "@/actions/send-email";
import { toast } from "sonner";

const contactSchema = z.object({
  name: z.string().min(2, "Spune-ne cum să îți spunem 🫶"),
  email: z.string().email("Te rugăm să adaugi un email valid"),
  phone: z.string().optional(),
  role: z.enum(["client", "therapist", "partner", "other"], {
    message: "Alege o opțiune",
  }),
  topic: z.enum(
    [
      "find-therapist",
      "social-fund",
      "collaboration",
      "press",
      "support",
      "other",
    ],
    {
      message: "Cu ce te putem ajuta?",
    }
  ),
  message: z
    .string()
    .min(10, "Dă-ne puțin context ca să îți răspundem cu sens 💬"),
  website: z.string().optional(), // honeypot
});

type ContactFormValues = z.infer<typeof contactSchema>;

export default function ContactForm() {
  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      role: "client",
      topic: "find-therapist",
      message: "",
      website: "",
    },
  });

  const recaptchaRef = useRef<ReCAPTCHA>(null);

  async function onSubmit(values: ContactFormValues) {
    if (values.website && values.website.trim() !== "") {
      console.warn("🚫 Bot submission blocked.");
      return;
    }

    const token = await recaptchaRef.current?.executeAsync();
    recaptchaRef.current?.reset();
    if (!token) {
      toast.error("Verificarea CAPTCHA a eșuat. Încearcă din nou 🙏");
      return;
    }

    const payload = { ...values, token };

    try {
      await toast.promise(
        sendEmail(payload),
        {
          loading: "Se trimite mesajul...",
          success: "Mesajul tău a fost trimis! Mulțumim 💛",
          error: "A apărut o eroare la trimitere. Încearcă din nou 🙏",
        }
      );
      form.reset();
    } catch (err) {
      console.error("Email send failed:", err);
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <input
          type="text"
          tabIndex={-1}
          autoComplete="off"
          {...form.register("website")}
          aria-hidden="true"
          style={{
            display: "none",
            position: "absolute",
            left: "-9999px",
          }}
        />

        <ReCAPTCHA
          ref={recaptchaRef}
          size="invisible"
          sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY!}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Nume complet</FormLabel>
                <FormControl>
                  <Input
                    placeholder="Nume și prenume"
                    className="rounded-2xl border-[#E9C46A]/60 focus-visible:ring-[#2A9D8F]/40"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    placeholder="exemplu@mindtale.ro"
                    className="rounded-2xl border-[#E9C46A]/60 focus-visible:ring-[#2A9D8F]/40"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Telefon (opțional)</FormLabel>
                <FormControl>
                  <Input
                    type="tel"
                    placeholder="+40..."
                    className="rounded-2xl border-[#E9C46A]/40 focus-visible:ring-[#2A9D8F]/40"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="role"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Calitatea în care ne contactezi</FormLabel>
                <FormControl>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <SelectTrigger className="rounded-2xl border-[#E9C46A]/60 focus-visible:ring-[#2A9D8F]/40">
                      <SelectValue placeholder="Selectează" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="client">Caut un terapeut</SelectItem>
                      <SelectItem value="therapist">
                        Sunt psihoterapeut
                      </SelectItem>
                      <SelectItem value="partner">
                        Reprezint o organizație / companie
                      </SelectItem>
                      <SelectItem value="other">Alt rol</SelectItem>
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="topic"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Subiect</FormLabel>
              <FormControl>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <SelectTrigger className="rounded-2xl border-[#E9C46A]/60 focus-visible:ring-[#2A9D8F]/40">
                    <SelectValue placeholder="Despre ce este mesajul tău?" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="find-therapist">
                      Vreau să găsesc un terapeut
                    </SelectItem>
                    <SelectItem value="social-fund">
                      Întrebări despre Fondul Social Mindtale
                    </SelectItem>
                    <SelectItem value="collaboration">
                      Colaborare / parteneriat
                    </SelectItem>
                    <SelectItem value="press">Presă / evenimente</SelectItem>
                    <SelectItem value="support">
                      Suport tehnic / cont
                    </SelectItem>
                    <SelectItem value="other">Alt subiect</SelectItem>
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="message"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Mesajul tău</FormLabel>
              <FormControl>
                <Textarea
                  rows={5}
                  placeholder="Spune-ne, pe scurt, cum te putem ajuta..."
                  className="resize-none rounded-2xl border-[#E9C46A]/60 focus-visible:ring-[#2A9D8F]/40"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 justify-between pt-2">
          <Button
            type="submit"
            className={cn(
              "px-8 py-3 rounded-full font-semibold text-white bg-[#E76F51] hover:bg-[#d75a42] shadow-md hover:shadow-lg transition-all",
              "flex items-center gap-2"
            )}
            disabled={form.formState.isSubmitting}
          >
            {form.formState.isSubmitting ? "Se trimite..." : "Trimite mesajul"}
            <span>➜</span>
          </Button>

          <p className="text-xs text-[#264653]/60 max-w-xs">
            Îți răspundem în maximum 2 zile lucrătoare. În caz de urgență, te
            rugăm să contactezi serviciile medicale de specialitate.
          </p>
        </div>
      </form>
    </Form>
  );
}
